import RegionSelector from './fragments/region-selector/RegionSelector'
import CustomAbout from './fragments/custom-about/CustomAbout'
import DisasterRegionService from './disaster-region-service'
import Vue from '@/common/prepared-vue.js'
import GeoUtils from '@/support/geo-utils'
import config from './config'
import Leaflet from 'leaflet'
import './resources/ors-disaster.css'
import {EventBus} from '@/common/event-bus'


/**
 * ORSDisaster class
 */
class ORSDisaster {

  /**
   * ORSDisaster constructor
   */
  constructor (vueInstance) {
    this.vueInstance = vueInstance
    this.featuresAdded = false
    this.regionSelectorAppended = false
    this.map = null
    this.disasterFeatureGroup = Leaflet.featureGroup()
    this.loadDisasterRegions()
    this.setCustomMessages()
    EventBus.$on('localeChanged', (locale) => {this.setCustomMessages(locale)})
  }

  /**
   * Set custom messages for current locale
   */
  setCustomMessages (locale) {
    locale = locale || this.vueInstance.$i18n.locale
    let messages = this.vueInstance.$i18n.messages[locale]
    messages.maps.aboutTitle = this.vueInstance.$i18n.messages[locale].orsDisaster.aboutTitle
    this.vueInstance.$i18n.setLocaleMessage(locale, messages)
  }

  /**
   * Load disaster regions
   * @returns {Promise}
   */
  loadDisasterRegions () {
    let context = this
    return new Promise((resolve) => {
      if (!context.vueInstance.$store.getters.disasterRegions) {
        DisasterRegionService.query().then(response => {
          context.vueInstance.$store.commit('disasterRegions', response.data)
          context.setDisasterRegionFromAppRoute()
          resolve(context.vueInstance.$store.getters.disasterRegions)
        })
      } else {
        resolve(context.vueInstance.$store.getters.regionSelected)
      }
    })
  }

  /**
   * Set disaster area based on parameter present in app route (URL)
   */
  setDisasterRegionFromAppRoute (){
    if (!this.vueInstance.$store.getters.regionSelected && this.vueInstance.$store.getters.appRouteData.options) {
      let regionId = this.vueInstance.$store.getters.appRouteData.options.disasterRegion
      if (regionId && this.vueInstance.$store.getters.disasterRegions[regionId]) {
        const region = this.vueInstance.$store.getters.disasterRegions[regionId]
        this.setSelectedRegion(region)
      }
    }
  }

  /**
   * Adjust the routing args before a request is made.
   * For disaster areas, an specific service is being used for routing, instead of
   * default ors api.
   * @param {*} args
   * @returns {Promise}
   */
  routingArgsCreated (args) {
    let context = this
    return new Promise((resolve) => {
      this.loadDisasterRegions().then(() => {
        let serviceUrl = `${config.baseDisasterUrl}${context.vueInstance.$store.getters.regionSelected[config.regionIdField]}/v2`
        args.portal_options = {'base_path': serviceUrl}
        resolve(args)
      })
    })
  }

  /**
   * Adjust the isochrones args before a request is made.
   * For disaster areas, an specific service is being used for isochrones, instead of
   * default ors api.
   * @param {*} args
   * @returns {Promise}
   */
  isochronesArgsCreated (args) {
    let context = this
    return new Promise((resolve) => {
      this.loadDisasterRegions().then(() => {
        let serviceUrl = `${config.baseDisasterUrl}${context.vueInstance.$store.getters.regionSelected[config.regionIdField]}/v2`
        args.portal_options = {'base_path': serviceUrl}
        args.attributes = []
        resolve(args)
      })
    })
  }

  /**
   * Load disaster region and add region selector when map is ready
   * @param {*} hookData
   */
  mapReady (hookData) {
    if (!this.featuresAdded) {
      this.map = hookData.map
      let context = this
      this.loadDisasterRegions().then(() => {
        context.addRegionSelector(hookData)
        this.featuresAdded = true
      })
    }
  }

  /**
   * Add disaster region parameter to app route (URL)
   * @param {*} options
   */
  afterGetRouteOptions (options) {
    options.disasterRegion = this.vueInstance.$store.getters.regionSelected[config.regionIdField]
  }

  /**
   * Set selected region
   * @param {*} region
   */
  setSelectedRegion (region) {
    if (region) {
      this.vueInstance.$store.commit('regionSelected', region)
      let geometryCoords = region.geojson.geometry.coordinates
      let holes = []
      for (let key in geometryCoords) {
        holes.push(GeoUtils.switchLatLonIndex(geometryCoords[key]))
      }

      var coords = [[[90, -180],[90, 180],[-90, 180],[-90, -180]], holes]

      this.disasterFeatureGroup.clearLayers()
      Leaflet.polygon(coords, {color:'red', fillColor: '#000', opacity: 1, weight: 1, fillOpacity: 0.3 }).addTo(this.disasterFeatureGroup)
      this.disasterFeatureGroup.addTo(this.map)
      let bounds = Leaflet.geoJSON(region.geojson).getBounds()
      this.map.setMaxBounds(bounds)
      this.map.fitBounds(bounds, { padding: [20, 20], maxZoom: 18 })
    }
  }

  /**
   * Add region selector component to map view
   * @param {*} hookData
   */
  addRegionSelector (hookData) {
    if (!this.featuresAdded) {
      var ComponentClass = Vue.extend(RegionSelector)
      let props = {
        regions: this.vueInstance.$store.getters.disasterRegions,
        regionSelected: this.vueInstance.$store.getters.regionSelected
      }

      var instance = new ComponentClass({ propsData: props, i18n: this.vueInstance.$i18n, store: this.vueInstance.$store})
      instance.$on(['regionSelected'], (region) => {
        this.setSelectedRegion(region)
      })
      instance.$mount() // pass nothing
      hookData.context.$refs.customMapControlsContainer.appendChild(instance.$el)
    }
  }

  /**
   * Customize floating menu items
   * @param {*} menuItems
   * @returns
   */
  floatingMenuItemsDefined (menuItems) {
    menuItems = menuItems.filter(function( item ) {
      return item.id !== 'openrouteserviceForDisasters'
    })
    let aboutIndex = menuItems.findIndex((item => item.id == 'about'))
    menuItems[aboutIndex].title = this.vueInstance.$t('orsDisaster.aboutTitle')
    return menuItems
  }

  /**
   * Customize about section
   * @param {*} hookData
   */
  aboutContentDefined (hookData) {
    var ComponentClass = Vue.extend(CustomAbout)
    var instance = new ComponentClass({i18n: this.vueInstance.$i18n, store: this.vueInstance.$store})
    instance.$mount() // pass nothing
    hookData.customAbout.appendChild(instance.$el)
  }
}
// export the ORSDisaster
export default ORSDisaster
